import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { IconCard, IconClose } from "../../components/Icons";
import { useAuth } from "../../context/AuthContext";
import { SubscriptionDetailResponse } from "../../types/subscription";
import InvoicesTable from "./components/InvoicesTable";
import SubscriptionActionsBar from "./components/SubscriptionActionsBar";
import SubscriptionDataCard from "./components/SubscriptionDataCard";
import SubscriptionStatusBadge from "./components/SubscriptionStatusBadge";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com/customers/";

export default function SubscriptionDetail() {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<SubscriptionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!subscriptionId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await request<SubscriptionDetailResponse>(
        `/api/admin/operations/subscriptions/${subscriptionId}`,
        { token },
      );
      setData(response);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(
          err.status === 404
            ? "Suscripción no encontrada"
            : `Error HTTP ${err.status}`,
        );
      } else {
        setError("No se pudo cargar la suscripción");
      }
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, token]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div style={{ padding: "1.5rem", color: "#73879C" }}>
        Cargando suscripción…
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: "1.5rem", color: "#b32424" }}>{error}</div>;
  }
  if (!data) return null;

  const { subscription, leadUlid } = data;
  return (
    <div style={{ padding: "1rem", maxWidth: 1600, margin: "0 auto" }}>
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.06)",
          padding: "0.9rem 1.1rem",
          marginBottom: "0.75rem",
          display: "flex",
          gap: "0.95rem",
          alignItems: "center",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              flexWrap: "wrap",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: "1.05rem",
                fontWeight: 600,
                color: "#1f2933",
              }}
            >
              Suscripción · {subscription.leadEmail}
            </h1>
            <SubscriptionStatusBadge status={subscription.status} />
          </div>
          <div
            style={{
              marginTop: "0.25rem",
              fontSize: ".8rem",
              color: "#73879C",
              display: "flex",
              gap: "0.85rem",
              flexWrap: "wrap",
            }}
          >
            <span style={{ fontFamily: "monospace" }}>{subscription.id}</span>
            {leadUlid && (
              <Link
                to={`/leads/${leadUlid}`}
                style={{ color: "#0069d9", textDecoration: "none" }}
              >
                ← Volver al lead
              </Link>
            )}
            <a
              href={`${STRIPE_DASHBOARD}${subscription.paymentExternalId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0069d9",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: ".25rem",
              }}
            >
              <IconCard style={{ width: ".9rem", height: ".9rem" }} />
              Abrir cliente en Stripe
            </a>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          title="Volver"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2rem",
            height: "2rem",
            padding: 0,
            borderRadius: "0.45rem",
            background: "#f8f9fb",
            color: "#1f2933",
            border: "1px solid #dee2e6",
            cursor: "pointer",
          }}
        >
          <IconClose />
        </button>
      </header>

      <SubscriptionActionsBar data={data} onReload={load} />
      <SubscriptionDataCard subscription={subscription} />
      <InvoicesTable subscriptionId={subscription.id} />
    </div>
  );
}
